import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { LedgerHeader, LedgerLineItem } from '../../../types';
import { Search, FileText, RotateCcw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useConfirm } from '../../../components/ui/ConfirmProvider';


// Helper for formatting currency
const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
    }).format(amount);
};

export function BinTab() {
    const [expenses, setExpenses] = useState<(LedgerHeader & { ledger_line_items: LedgerLineItem[] })[]>([]);
    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<(LedgerHeader & { ledger_line_items: LedgerLineItem[] })[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        fetchDeletedExpenses();
    }, []);

    const fetchDeletedExpenses = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('ledger_headers')
                .select('*, ledger_line_items(category)')
                .eq('transaction_type', 'expense')
                .not('deleted_at', 'is', null)
                .order('deleted_at', { ascending: false })
                .limit(20);

            if (error) throw error;
            setExpenses(data || []);
        } catch (err) {
            console.error('Error fetching deleted expenses:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (term: string) => {
        setSearchTerm(term);
        if (!term) {
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        try {
            const { data, error } = await supabase
                .from('ledger_headers')
                .select('*, ledger_line_items(category)')
                .eq('transaction_type', 'expense')
                .not('deleted_at', 'is', null)
                .or(`vendor.ilike.%${term}%, total_gross.eq.${parseFloat(term) || 0}`)
                .order('deleted_at', { ascending: false })
                .limit(20);

            if (!error) {
                setSearchResults(data || []);
            }
        } catch (err) {
            console.error("Search error", err);
        }
    };

    const handleRestore = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            const { error } = await supabase
                .from('ledger_headers')
                .update({ deleted_at: null })
                .eq('id', id);

            if (error) throw error;

            setExpenses(prev => prev.filter(exp => exp.id !== id));
            if (isSearching) {
                setSearchResults(prev => prev.filter(exp => exp.id !== id));
            }
        } catch (err) {
            console.error('Error restoring:', err);
            toast.error('Failed to restore');
        }
    };

    const handlePermanentDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!await confirm({ title: 'Delete permanently?', message: 'The invoice and its stored file are removed for good. This cannot be undone.', confirmText: 'Delete for good', isDangerous: true })) return;

        try {
            const { error } = await supabase
                .from('ledger_headers')
                .delete()
                .eq('id', id);

            if (error) throw error;

            setExpenses(prev => prev.filter(exp => exp.id !== id));
            if (isSearching) {
                setSearchResults(prev => prev.filter(exp => exp.id !== id));
            }
        } catch (err) {
            console.error('Error permanently deleting:', err);
            toast.error('Failed to delete permanently');
        }
    };

    const displayData = isSearching ? searchResults : expenses;

    return (
        <div className="space-y-6">
            {/* Archive Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-4 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Search Bin (Vendor or Amount)..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full bg-card border border-line rounded-lg pl-10 pr-4 py-3 text-fg placeholder:text-fg-4 focus:outline-none focus:border-accent transition-colors"
                />
            </div>

            {/* Table */}
            <div className="bg-card/30 border border-line/50 rounded-xl overflow-hidden backdrop-blur-sm">
                <div className="overflow-x-auto">
                <table className="w-full text-left text-sm min-w-[640px]">
                    <thead className="bg-card/50 border-b border-line text-fg-3 font-medium">
                        <tr>
                            <th className="px-6 py-4">Deleted</th>
                            <th className="px-6 py-4">Vendor</th>
                            <th className="px-6 py-4">Category</th>
                            <th className="px-6 py-4 text-right">Total (Inc. VAT)</th>
                            <th className="px-6 py-4 text-center">Receipt</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-line/50">
                        {loading ? (
                            <tr><td colSpan={6} className="px-6 py-8 text-center text-fg-4">Loading bin...</td></tr>
                        ) : displayData.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-8 text-center text-fg-4">Bin is empty.</td></tr>
                        ) : (
                            displayData.map((expense) => {
                                // Determine category display
                                const categories = Array.from(new Set(expense.ledger_line_items?.map((l: LedgerLineItem) => l.category) || []));
                                const categoryLabel = categories.length > 1 ? 'Multiple' : (categories[0] || 'Uncategorised');

                                return (
                                    <tr key={expense.id} className="hover:bg-raised/30 transition-colors">
                                        <td className="px-6 py-4 text-fg-2">
                                            {new Date(expense.deleted_at as string).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 font-medium text-fg line-through opacity-60">
                                            {expense.vendor}
                                        </td>
                                        <td className="px-6 py-4 opacity-60">
                                            <span className="px-2 py-1 bg-raised rounded text-xs text-fg-3 border border-line-strong/50">
                                                {categoryLabel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono text-fg-2 opacity-60">
                                            {formatCurrency(expense.total_gross)}
                                        </td>
                                        <td className="px-6 py-4 text-center text-fg-4 hover:text-accent cursor-pointer">
                                            <FileText className="w-4 h-4 mx-auto" />
                                        </td>
                                        <td className="px-6 py-4 flex items-center justify-end gap-2">
                                            <button
                                                title="Restore"
                                                onClick={(e) => handleRestore(expense.id, e)}
                                                className="p-2 text-fg-4 hover:text-success hover:bg-success/10 rounded-lg transition-colors"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                            </button>
                                            <button
                                                title="Permanently Delete"
                                                onClick={(e) => handlePermanentDelete(expense.id, e)}
                                                className="p-2 text-fg-4 hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}
